import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import Container from '../components/ui/Container';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import { products } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import { ProductSkeleton } from '../components/ui/Skeleton';

const Shop = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialAgeParam = searchParams.get('age') || 'all';
  const initialCatParam = searchParams.get('category') || 'all';

  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(initialAgeParam);
  const [activeCategory, setActiveCategory] = useState(initialCatParam);
  const [sortBy, setSortBy] = useState('featured');

  useEffect(() => {
    // Simulate network delay
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, [activeFilter, activeCategory, sortBy]);

  const filters = [
    { id: 'all', label: 'All Ages' },
    { id: '3-5', label: '3-5 Years' },
    { id: '6-8', label: '6-8 Years' },
    { id: '9-12+', label: '9-12+ Years' },
  ];

  const categories = ['all', ...new Set(products.map(p => p.category.toLowerCase()))];

  const filteredProducts = useMemo(() => {
    let result = products.filter(p => {
      const ageMatch = activeFilter === 'all' || p.ageRange === activeFilter;
      const catMatch = activeCategory === 'all' || p.category.toLowerCase() === activeCategory.toLowerCase();
      return ageMatch && catMatch;
    });

    if (sortBy === 'price-low') result.sort((a, b) => a.price - b.price);
    if (sortBy === 'price-high') result.sort((a, b) => b.price - a.price);
    
    return result;
  }, [activeFilter, activeCategory, sortBy]);

  return (
    <div className="bg-slate-50 min-h-screen pt-40 pb-24">
      <Container>
        <div className="mb-12">
          <div className="flex flex-col md:flex-row justify-between items-end gap-6 border-b-4 border-ink-navy pb-10">
            <div className="max-w-2xl">
              <h1 className="text-5xl md:text-6xl font-black text-ink-navy mb-4 uppercase italic">Inventory</h1>
              <p className="text-slate-500 font-bold text-lg">Curated kits for purposeful exploration. Every item is safety-tested and education-approved.</p>
            </div>
            <div className="flex gap-4">
              <div className="bg-white border-2 border-ink-navy rounded-xl px-4 py-2 flex items-center gap-3 shadow-[2px_2px_0_0_#1e1b4b]">
                <span className="text-xs font-black uppercase text-slate-400">Sort:</span>
                <select 
                  className="font-bold text-ink-navy bg-transparent focus:outline-none cursor-pointer"
                  value={sortBy}
                  onChange={(e) => {
                    setIsLoading(true);
                    setSortBy(e.target.value);
                  }}
                >
                  <option value="featured">Featured</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Filters */}
          <aside className="w-full lg:w-72 flex-shrink-0 space-y-10">
            <div>
              <h3 className="font-black text-xs uppercase tracking-widest text-sky-blue mb-6 flex items-center gap-2">
                <SafeIcon name="Target" /> Age Range
              </h3>
              <div className="flex flex-wrap lg:flex-col gap-3">
                {filters.map(filter => (
                  <button
                    key={filter.id}
                    onClick={() => {
                      setIsLoading(true);
                      setActiveFilter(filter.id);
                    }}
                    className={`text-left px-5 py-3 rounded-2xl border-2 transition-all font-black text-sm uppercase tracking-tight ${
                      activeFilter === filter.id 
                        ? 'bg-lemon-yellow border-ink-navy text-ink-navy shadow-[4px_4px_0_0_#1e1b4b]' 
                        : 'bg-white border-slate-200 text-slate-400 hover:border-ink-navy hover:text-ink-navy'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-black text-xs uppercase tracking-widest text-sky-blue mb-6 flex items-center gap-2">
                <SafeIcon name="Grid" /> Subject Area
              </h3>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => {
                      setIsLoading(true);
                      setActiveCategory(cat);
                    }}
                    className={`px-4 py-2 rounded-xl text-xs font-black border-2 transition-all uppercase tracking-widest ${
                      activeCategory === cat
                        ? 'bg-sky-blue border-ink-navy text-white shadow-[3px_3px_0_0_#1e1b4b]'
                        : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6 bg-ink-navy rounded-[30px] border-4 border-ink-navy shadow-xl text-white">
              <SafeIcon name="Zap" className="text-3xl text-lemon-yellow mb-4" />
              <h4 className="font-black text-lg mb-2 leading-tight">Need help choosing?</h4>
              <p className="text-slate-400 text-sm font-bold mb-6">Our guide helps you match a kit to your child's specific developmental stage.</p>
              <Button onClick={() => window.location.href='#/guide'} variant="primary" size="sm" className="w-full">
                Open Guide
              </Button>
            </div>
          </aside>

          {/* Main List */}
          <div className="flex-1">
            <div className="mb-8 flex items-center gap-4">
              <Badge variant="outline" className="py-1 px-4">{filteredProducts.length} Kits Found</Badge>
              {(activeFilter !== 'all' || activeCategory !== 'all') && (
                <button 
                  onClick={() => { 
                    setIsLoading(true);
                    setActiveFilter('all'); 
                    setActiveCategory('all'); 
                  }}
                  className="text-xs font-black text-red-500 uppercase tracking-widest hover:underline"
                >
                  Clear Filters
                </button>
              )}
            </div>
            
            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
                {[...Array(6)].map((_, i) => <ProductSkeleton key={i} />)}
              </div>
            ) : filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
                {filteredProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-32 bg-white rounded-[40px] border-4 border-dashed border-slate-200">
                <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon name="Search" className="text-3xl text-slate-300" />
                </div>
                <h3 className="text-2xl font-black text-ink-navy mb-2 uppercase">No matches found</h3>
                <p className="text-slate-500 font-bold mb-8">Try broadening your search or resetting the filters.</p>
                <Button onClick={() => { setIsLoading(true); setActiveFilter('all'); setActiveCategory('all'); }}>
                  See All Kits
                </Button>
              </div>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Shop;